// The diagram render cache (TODO §21/§5.2) — one serialized SVG string per diagram, keyed
// `diagramDocId:rev`, recomputed on a debounce after each edit and read by EVERY render
// path: the window at rest, the other window, the nav thumbnails, the print layer,
// presentation, the off-screen capturer and the HTML export.
//
// This is what §5.1's one-instance rule buys. Exactly one Excalidraw editor is ever mounted
// — the window being edited — and everything else is a static SVG, so memory, export, print,
// thumbnails and presentation do not each need their own answer. And unlike the raster
// snapshots grids need (a cross-origin iframe leaves no choice), this is vector: same-origin,
// crisp at any zoom, and it prints.
//
// Deliberately a module-level singleton rather than React state. The print and presentation
// overlays are separate subtrees, the off-screen capturer is a third, and the HTML export is
// not a component at all; a context would have to be threaded to all four and would still
// leave the exporter out.

import { loadDiagramEngine } from "@/lib/diagram-engine";
import { cacheKey, frameElement } from "@/lib/diagram";
import { getAsset } from "@/lib/idb";
import type { DiagramDocument } from "@/types/diagram";

export interface DiagramSvg {
  // The cache key this was rendered for; a mismatch against `cacheKey(docId, doc)` means
  // the entry is stale and a re-render is already in flight.
  key: string;
  svg: string;
  // Frame dimensions in scene units, so a consumer can letterbox (§3.4) without parsing the
  // SVG. Null when the scene has no frame — the export then clipped to the element bounds.
  width: number | null;
  height: number | null;
}

const cache = new Map<string, DiagramSvg>();
// docId → the cache key whose render is currently in flight. One entry per doc: a newer edit
// replaces it, and the older render discards its result when it finds the key has moved on.
const inflightKeys = new Map<string, string>();
const listeners = new Set<() => void>();

// A monotonic version of the cache as a whole. `useSyncExternalStore` needs a snapshot that
// is `Object.is`-stable between notifications; a number is the cheapest one, and a consumer
// that cares WHICH entry changed re-reads it by key anyway.
let version = 0;

export function diagramCacheVersion(): number {
  return version;
}

function emit(): void {
  version += 1;
  for (const listener of listeners) listener();
}

// React subscription for `useSyncExternalStore`. One global notification per completed
// render is enough: the entries are keyed, so a component re-reading its own docId sees the
// same object identity unless ITS diagram changed.
export function subscribeDiagramSvgs(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// The cached SVG for this doc, or null when nothing has been rendered for it yet. Returns a
// STALE entry rather than null while a re-render is in flight, so an edit does not blank the
// window for a frame — the old drawing is a better placeholder than an empty box.
export function peekDiagramSvg(docId: string): DiagramSvg | null {
  return cache.get(docId) ?? null;
}

// §5.3 Is this doc's CURRENT revision rendered? The thumbnail capturer gates on this: it
// rasterizes an off-screen `<Slide>` synchronously, so a diagram whose SVG is not yet in the
// cache would be captured as an empty box — and that empty box would then be cached as the
// slide's thumbnail. Same shape as the existing "retry until the embed has loaded" logic in
// `thumbnailStore`, deliberately, rather than a second mechanism.
export function isDiagramSvgFresh(
  docId: string,
  doc: DiagramDocument,
): boolean {
  return cache.get(docId)?.key === cacheKey(docId, doc);
}

// === Images inside a diagram (§4.4) ===
//
// Excalidraw's `files` are dataURLs keyed by fileId. We mint our own ids
// (`generateIdForFile`) and put the BLOB in the existing `assets` store, so the doc carries
// assetIds and the render path rebuilds the map on demand. §4.4.4: `exportToSvg` takes
// `files`, and an export path that skips this loses the diagram's images in PDF, HTML and
// presentation while looking correct in the editor — the worst possible failure shape.
//
// Memoised by assetId: a blob → dataURL conversion per render would re-read IndexedDB and
// re-base64 the same bytes on every keystroke.
const fileCache = new Map<string, { dataURL: string; mimeType: string }>();

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

// `BinaryFiles`-shaped, built from our asset store. Typed loosely on our side and crossed
// via `unknown` at the `exportToSvg` boundary, exactly as `GriddDocument` crosses into the
// embed.
export async function loadDiagramFiles(
  fileIds: readonly string[],
): Promise<Record<string, unknown>> {
  const files: Record<string, unknown> = {};
  for (const id of fileIds) {
    let entry = fileCache.get(id);
    if (!entry) {
      const record = await getAsset(id);
      if (!record) continue; // a missing blob renders as a gap, never as a crash
      entry = {
        dataURL: await blobToDataUrl(record.blob),
        mimeType: record.mime,
      };
      fileCache.set(id, entry);
    }
    files[id] = {
      id,
      dataURL: entry.dataURL,
      mimeType: entry.mimeType,
      created: 0,
    };
  }
  return files;
}

// Drop a cached image. Called when a diagram's asset is deleted so a re-added file with a
// recycled id (there are none today, but ids are ours to mint) cannot serve stale bytes.
export function forgetDiagramFile(assetId: string): void {
  fileCache.delete(assetId);
}

// === Rendering ===

interface RenderOptions {
  // §5.4 Leave fonts INLINED for the HTML export — that file must open on a machine that has
  // never seen the deck — and SKIP them for in-app renders, where the fonts are already
  // loaded and inlining them into every thumbnail SVG makes each one hundreds of KB.
  inlineFonts?: boolean;
}

async function renderSvg(
  doc: DiagramDocument,
  options: RenderOptions,
): Promise<{ svg: string; width: number | null; height: number | null }> {
  const engine = await loadDiagramEngine();
  const files = await loadDiagramFiles(doc.fileIds);
  // `restore()` is Excalidraw's own forward-compatibility ladder (§4.2.1) — run it on the way
  // OUT of storage too, not just into the editor, so a scene written by an older package
  // version renders through the same upgrade path it would be edited through.
  const restored = engine.restore(
    {
      elements: doc.elements as never,
      appState: doc.appState as never,
      files: files as never,
    },
    null,
    null,
  );
  const frame = frameElement(doc);
  const element = await engine.exportToSvg({
    elements: restored.elements,
    appState: restored.appState,
    files: files as never,
    // §3.2 Every render at rest clips to the frame EXACTLY. So the thing on the slide is
    // always the frame's contents, never "whatever the author happened to be looking at",
    // and the scratch margin outside it (§3.5) stays out of the PDF.
    exportingFrame: (frame ?? null) as never,
    exportPadding: 0,
    ...(options.inlineFonts ? {} : { skipInliningFonts: true as const }),
  });
  // §13.3 A screen reader should get the diagram's labels, not a pile of <path>s.
  const label = engine.getTextFromElements(restored.elements);
  element.setAttribute("role", "img");
  if (label.trim()) element.setAttribute("aria-label", label.trim());
  const frameBox = frame as { width?: number; height?: number } | null;
  return {
    svg: new XMLSerializer().serializeToString(element),
    width: typeof frameBox?.width === "number" ? frameBox.width : null,
    height: typeof frameBox?.height === "number" ? frameBox.height : null,
  };
}

// Make sure the cache holds a CURRENT render for this doc, starting one if not. Idempotent
// and safe to call on every render: a call for a key already cached or already in flight is
// a map lookup. Fire-and-forget by design — the caller re-reads through `peekDiagramSvg`
// when the subscription fires.
export function ensureDiagramSvg(docId: string, doc: DiagramDocument): void {
  const key = cacheKey(docId, doc);
  if (cache.get(docId)?.key === key) return;
  if (inflightKeys.get(docId) === key) return;
  inflightKeys.set(docId, key);
  void renderSvg(doc, {})
    .then((result) => {
      // A newer edit may have landed while this render ran. Only the newest wins — and it
      // has already been scheduled, so dropping this result loses nothing.
      if (inflightKeys.get(docId) !== key) return;
      cache.set(docId, { key, ...result });
      emit();
    })
    .catch((err) => {
      console.warn("[diagram] SVG render failed", docId, err);
    })
    .finally(() => {
      if (inflightKeys.get(docId) === key) inflightKeys.delete(docId);
    });
}

// The export path's render (§11.2). Bypasses the cache entirely: it wants fonts INLINED
// (§5.4), which the in-app entries deliberately do not have, and it runs once per export
// rather than per keystroke. Returns null rather than throwing so one unrenderable diagram
// costs that window, not the whole export.
export async function renderDiagramSvgForExport(
  doc: DiagramDocument,
): Promise<string | null> {
  try {
    const { svg } = await renderSvg(doc, { inlineFonts: true });
    return svg;
  } catch (err) {
    console.warn("[diagram] export render failed", err);
    return null;
  }
}

// Forget a diagram's cached render. Called when its window is cleared, so a recycled docId
// (import/duplicate mint fresh ones, but nothing stops a future caller) cannot show the old
// drawing, and so a long editing session does not hold every deleted diagram in memory.
export function dropDiagramSvg(docId: string): void {
  cache.delete(docId);
  inflightKeys.delete(docId);
  emit();
}
